<!-- Copyright (c) 2019-2026 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<template>
  <BAlert
    class="fr-alert"
    v-bind="$props"
    :data-testid="testId"
    aria-live="assertive">
    <template #dismiss>
      <FrIcon
        :aria-label="$t('common.close')"
        :outlined="false"
        name="close" />
    </template>
    <img v-if="variant != 'nothing'" :src="alertIconFile" class="alert-icon mr-2" :alt="alertIconAltText" />
    <!-- <FrIcon
      v-if="showIcon"
      class="mr-2"
      :name="alertIcon"
    /> -->
    <!-- @slot Text that the alert will contain -->
    <slot />
  </BAlert>
</template>

<script>
import { BAlert } from 'bootstrap-vue';
import FrIcon from '@forgerock/platform-shared/src/components/Icon';
/**
 * Extends Bootstrap alert to provide alternative icon and styling
 */
export default {
  name: 'Alert',
  components: {
    BAlert,
    FrIcon,
  },
  props: {
    /**
     * Can be closed or dismissed by a user
     */
    dismissible: {
      type: Boolean,
      default: true,
    },
    /**
     * Will have a fade animation on dismiss
     */
    fade: {
      type: Boolean,
      default: true,
    },
    /**
     * Is visible on display
     */
    show: {
      type: Boolean,
      default: true,
    },
    /**
     * Accessibility name
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Type of alert
     * @values primary, secondary, success, danger, warning, information, light, dark
     */
    variant: {
      type: String,
      default: 'primary',
    },
    icon: {
      type: String,
      default: null,
    },
    /**
     * Should the Alert component render an icon
     */
    showIcon: {
      type: Boolean,
      default: true,
    },
    testId: {
      type: String,
      default: 'FrAlert',
    },
  },
  computed: {
    alertIcon() {
      let icon;

      switch (this.variant) {
        case 'info':
        case 'primary':
          icon = 'info';
          break;
        case 'danger':
        case 'error':
          icon = 'cancel';
          break;
        case 'warning':
          icon = 'warning_amber';
          break;
        case 'success':
          icon = 'check_circle';
          break;
        default:
          icon = 'message';
      }

      return this.icon || icon;
    },
    alertIconFile() {
      let iconFileName;

      switch(this.variant) {
        case 'success':
          iconFileName = 'succes.svg';
          break;
        default:
          iconFileName = 'erreur.svg';
      }

      return require(`../../assets/icons/${iconFileName}`);
    },
    alertIconAltText() {
      let iconAltText = '';

      switch(this.variant) {
        case 'success':
          iconAltText = 'common.success';
          break;
        default:
          iconAltText = 'common.error';
      }

      return this.$t(iconAltText);
    },
  },
};
</script>

<style lang="scss" scoped>
  .fr-alert {
    display: flex;
    line-height: 1.25;

    &.alert-primary {
      border-left: 5px solid $primary;
      background-color: $fr-alert-primary-bg-color;
      color: $fr-alert-text-color;
    }

    &.alert-secondary {
      border-left: 5px solid $secondary;
      background-color: $fr-alert-secondary-bg-color;
      color: $fr-alert-text-color;
    }

    &.alert-success {
      border: 1px solid $success;
      border-radius: 4px;
      background-color: $fr-alert-success-bg-color;
      color: $fr-alert-text-color;
    }

    &.alert-danger,
    &.alert-error {
      background-color: #FEE6E3;
      border: 1px solid #c6252a;
      color: #c6252a;
      border-radius:4px;
      padding: 0.875rem 1rem;
      color: #404040;
      font-weight: 600;
    }

    &.alert-warning {
      border-left: 5px solid $warning;
      background-color: $fr-alert-warning-bg-color;
      color: $fr-alert-text-color;
    }

    &.alert-info {
      border-left: 5px solid $info;
      background-color: $fr-alert-info-bg-color;
      color: $fr-alert-text-color;
    }

    &.alert-light {
      border-left: 5px solid $light;
      background-color: $fr-alert-light-bg-color;
      color: $fr-alert-text-color;
    }

    &.alert-dark {
      border-left: 5px solid $dark;
      color: $fr-alert-dark-text-color;
      background-color: $fr-alert-dark-bg-color;
    }

    :deep(.close) {
      opacity: 0.85;
      top: 4px;
      right: 4px;
      &:not(:disabled):not(.disabled):hover {
        opacity: 1;
      }
    }

    > img.alert-icon {
       width: 1rem;
      height: 1rem;
    }
  }
</style>
